<template>
  <div class="flex flex-col gap-6 p-4">
    <!-- Header + Date Filter -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">Humanos Digitais</h2>
        <p class="text-sm text-n-slate-11">Performance e uso dos agentes de IA</p>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model="startDate"
          type="date"
          class="px-3 py-1.5 text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-12"
          @change="fetchData"
        />
        <span class="text-xs text-n-slate-11">até</span>
        <input
          v-model="endDate"
          type="date"
          class="px-3 py-1.5 text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-12"
          @change="fetchData"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <span class="text-sm text-n-slate-11">Carregando dados...</span>
    </div>

    <template v-else-if="summary">
      <!-- KPI Cards -->
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div
          v-for="kpi in kpis"
          :key="kpi.label"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <p class="text-[10px] font-medium uppercase tracking-wide text-n-slate-10">{{ kpi.label }}</p>
          <p class="mt-1 text-xl font-bold text-n-slate-12">{{ kpi.value }}</p>
        </div>
      </div>

      <!-- Per-DH Table -->
      <div class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden">
        <div class="px-4 py-3 border-b border-n-weak">
          <h3 class="text-sm font-semibold text-n-slate-12">Por Humano Digital</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-xs">
            <thead>
              <tr class="border-b border-n-weak bg-n-background">
                <th class="px-4 py-2 text-left font-medium text-n-slate-11">Nome</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Status</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Conversas</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Msgs Enviadas</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Handoffs</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Tokens</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Custo (USD)</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Latência</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Sucesso</th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">Follow-ups</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="dh in perDh"
                :key="dh.id"
                class="border-b border-n-weak last:border-0 hover:bg-n-background/50"
              >
                <td class="px-4 py-2.5 font-medium text-n-slate-12">{{ dh.name }}</td>
                <td class="px-4 py-2.5 text-right">
                  <span
                    :class="dh.active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-n-alpha-2 text-n-slate-11'"
                    class="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium"
                  >
                    {{ dh.active ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-12">{{ dh.total_conversations }}</td>
                <td class="px-4 py-2.5 text-right text-n-slate-12">{{ dh.messages_sent }}</td>
                <td class="px-4 py-2.5 text-right text-n-slate-12">{{ dh.handed_off }}</td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">{{ formatNumber(dh.total_tokens) }}</td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">${{ dh.total_cost_usd }}</td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">{{ dh.avg_latency_ms }}ms</td>
                <td class="px-4 py-2.5 text-right">
                  <span v-if="dh.success_count + dh.error_count > 0" class="text-n-slate-12">
                    {{ Math.round(dh.success_count / (dh.success_count + dh.error_count) * 100) }}%
                  </span>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">{{ dh.followups_sent }}</td>
              </tr>
              <tr v-if="!perDh.length">
                <td colspan="10" class="px-4 py-8 text-center text-n-slate-10">
                  Nenhum Humano Digital configurado
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Daily Chart (simple bar representation) -->
      <div v-if="daily.length" class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Chamadas diárias à IA</h3>
        <div class="flex items-end gap-1 h-32">
          <div
            v-for="day in daily"
            :key="day.date"
            class="flex-1 bg-n-brand/80 rounded-t transition-all hover:bg-n-brand group relative"
            :style="{ height: dailyBarHeight(day.calls) + '%', minHeight: day.calls > 0 ? '4px' : '0' }"
            :title="`${day.date}: ${day.calls} chamadas, ${day.tokens} tokens, $${day.cost}`"
          />
        </div>
        <div class="flex justify-between mt-1">
          <span class="text-[9px] text-n-slate-10">{{ daily[0]?.date }}</span>
          <span class="text-[9px] text-n-slate-10">{{ daily[daily.length - 1]?.date }}</span>
        </div>
      </div>
    </template>

    <!-- Empty state -->
    <div v-else-if="!loading" class="flex flex-col items-center justify-center py-16">
      <p class="text-sm text-n-slate-11">Sem dados para o período selecionado</p>
    </div>
  </div>
</template>

<script>
/* global axios */
export default {
  data() {
    return {
      loading: true,
      summary: null,
      perDh: [],
      daily: [],
      startDate: this.defaultStartDate(),
      endDate: new Date().toISOString().slice(0, 10),
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    kpis() {
      if (!this.summary) return [];
      return [
        { label: 'Conversas', value: this.summary.total_conversations },
        { label: 'Msgs Enviadas', value: this.summary.total_messages_sent },
        { label: 'Handoffs', value: this.summary.total_handoffs },
        { label: 'Taxa Sucesso', value: `${this.summary.success_rate}%` },
        { label: 'Latência Média', value: `${this.summary.avg_latency_ms}ms` },
      ];
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    defaultStartDate() {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().slice(0, 10);
    },
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await axios.get(
          `/api/v2/accounts/${this.accountId}/digital_human_reports`,
          { params: { start_date: this.startDate, end_date: this.endDate } }
        );
        this.summary = data.summary;
        this.perDh = data.per_digital_human || [];
        this.daily = data.daily || [];
      } catch (e) {
        this.summary = null;
        this.perDh = [];
        this.daily = [];
      } finally {
        this.loading = false;
      }
    },
    formatNumber(n) {
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
      return n;
    },
    dailyBarHeight(calls) {
      const max = Math.max(...this.daily.map(d => d.calls), 1);
      return Math.round((calls / max) * 100);
    },
  },
};
</script>
